import { useState, useEffect, useRef, useCallback } from 'react';

interface UseDebouncedSearchOptions {
  delay?: number;
  minLength?: number;
  onSearchStart?: () => void;
  onSearchComplete?: () => void;
}

interface UseDebouncedSearchReturn {
  searchTerm: string;
  debouncedSearchTerm: string;
  isSearching: boolean;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
}

export function useDebouncedSearch(
  initialValue: string = '',
  options: UseDebouncedSearchOptions = {}
): UseDebouncedSearchReturn {
  const {
    delay = 300,
    minLength = 0,
    onSearchStart,
    onSearchComplete
  } = options;

  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);
  
  const timeoutRef = useRef<NodeJS.Timeout>();
  const searchStartedRef = useRef(false);

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Don't debounce if search term is shorter than minimum length
    if (searchTerm.length < minLength) {
      setDebouncedSearchTerm('');
      setIsSearching(false);
      if (searchStartedRef.current) {
        onSearchComplete?.();
        searchStartedRef.current = false;
      }
      return;
    }

    // Start search indicator if not already started
    if (!searchStartedRef.current && searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
      onSearchStart?.();
      searchStartedRef.current = true;
    }

    // Set up debounced update
    timeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
      if (searchStartedRef.current) {
        onSearchComplete?.();
        searchStartedRef.current = false;
      }
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm, delay, minLength, onSearchStart, onSearchComplete]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setIsSearching(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (searchStartedRef.current) {
      onSearchComplete?.();
      searchStartedRef.current = false;
    }
  }, [onSearchComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    isSearching,
    setSearchTerm,
    clearSearch
  };
}